import { useContext, useState } from "react";
import { Box, Button } from "@mui/material";
import { useGetFamilyDetails, useUpdateFamily } from "services/advisor";
import {
  Advisor,
  UserPermission,
  UserPermission as UserPermissionProto,
  Member as MemberProto,
} from "protogen/common_pb";
import { PlanUtilization } from "protogen/common_pb";
import {
  Family as FamilyProto,
  FamilyStatus,
  UpdateFamilyRequest,
} from "protogen/advisors_service_pb";
import { SectionProps } from "components/details-page/Section";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";
import SnackPack, { SnackbarMessage } from "components/common/SnackPack";
import { validateFamily } from "./utils";
import { updates } from "./updateUtils";
import EmbeddedForm from "./EmbeddedForm";
import FamilyCalendarDetails from "./FamilyCalendarDetails";
import FamilyBillingHistorySection from "./FamilyBillingHistorySection";
import FamilyPlanManagementSection from "./FamilyPlanManagementSection";
import { Family, familyProtoToFamily, FormErrors, Member } from "./types";

const FamilyDetails = ({
  family,
  familyOnly,
  contactOnly,
  primaryAdvisor,
  advisorPermissions,
  planUtilization,
}: {
  family: FamilyProto;
  familyOnly?: boolean;
  contactOnly?: string;
  // Should be required.
  primaryAdvisor: Advisor;
  advisorPermissions?: UserPermissionProto[];
  planUtilization: PlanUtilization;
}) => {
  const [snackPack, setSnackPack] = useState<readonly SnackbarMessage[]>([]);
  const [familyErrors, setFamilyErrors] = useState<FormErrors | null>(null);
  const [localFamily, setLocalFamily] = useState<FamilyProto>(family);
  const [localPermissions, setLocalPermissions] = useState<
    UserPermissionProto[]
  >(advisorPermissions || []);
  const memberToMember = (member: MemberProto): Member => ({
    ref: member.ref,
    firstName: member.firstName,
    lastName: member.lastName,
    phone: member.primaryPhone,
    email: member.primaryEmail,
    altEmails: member.alternateEmails,
    timezone: member.timezone,
    activityNotificationEmailsEnabled: member.activityNotificationEmailsEnabled,
  });

  const [familyData, setFamilyData] = useState<Family>(
    familyProtoToFamily(family, advisorPermissions || []),
  );
  const [members, setMembers] = useState<Member[]>(
    family.familyMembers.map(memberToMember),
  );

  const { request: requestRefreshFamily } = useGetFamilyDetails((r) => {
    if (r.family) {
      setFamilyData(familyProtoToFamily(r.family, r.advisorPermissions || []));
      setMembers(r.family.familyMembers.map(memberToMember));
      setLocalFamily(r.family);
      setLocalPermissions(r.advisorPermissions || []);
    }
  });

  const { request, loading } = useUpdateFamily(() => {});

  const _validate = () => {
    const famErrors = validateFamily(familyData);
    setFamilyErrors(famErrors);
    let hasErrors = Object.keys(famErrors).length !== 0;
    return !hasErrors;
  };
  const handleEditFamily = async () => {
    if (_validate()) {
      const r = updates(localFamily, familyData, members, localPermissions);
      const resp = await request(new UpdateFamilyRequest(r));
      if (resp) {
        await requestRefreshFamily({ familyRef: family.ref });
        setSnackPack((p) => [
          ...p,
          {
            message: "Family details updated successfully",
            saveNotification: true,
            key: new Date().getTime(),
          },
        ]);
      }
      if (r.updatedStatus || r.updatedAdvisorRef) {
        // Force a reload as broader state depends on the family status (sidebar, timetracker etc).
        window.location.reload();
      }
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <EmbeddedForm
        familyData={familyData}
        members={members}
        setFamilyData={(changes) =>
          setFamilyData((fd) => ({
            ...fd,
            ...changes,
          }))
        }
        setMembers={setMembers}
        familyErrors={familyErrors}
        handleSubmit={handleEditFamily}
        familyOnly={familyOnly}
        contactOnly={contactOnly}
        primaryAdvisor={primaryAdvisor}
        planUtilization={planUtilization}
      />
      <Box>
        <Button disabled={loading} onClick={handleEditFamily}>
          Save
        </Button>
      </Box>
      <SnackPack
        autoHideDuration={6000}
        snackPack={snackPack}
        alertSeverity="success"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
};

interface Props extends SectionProps {
  family: FamilyProto;
  advisorPermissions: UserPermission[];
  primaryAdvisor: Advisor;
  planUtilization: PlanUtilization;
}
export default ({
  family,
  primaryAdvisor,
  advisorPermissions,
  planUtilization,
}: Props) => {
  const currentUser = useContext(CurrentUserContext);
  const isAdvisor = currentUser?.ref === family.advisorRef;
  const isAdmin = isUserAdmin(currentUser);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "48px",
        maxWidth: "900px",
        margin: "auto",
      }}
    >
      <FamilyDetails
        family={family}
        advisorPermissions={advisorPermissions}
        primaryAdvisor={primaryAdvisor}
        planUtilization={planUtilization}
      />
      {(isAdvisor || isAdmin) && family.status === FamilyStatus.ACTIVE ? (
        <>
          <FamilyPlanManagementSection
            family={family}
            refresh={() => window.location.reload()}
          />
        </>
      ) : null}
      <FamilyCalendarDetails family={family} />
      {isAdvisor || isAdmin ? (
        <>
          <FamilyBillingHistorySection family={family} />
        </>
      ) : null}
    </Box>
  );
};
